import { bytesToNDigits } from 'lib/bytes';
import { equals, values } from 'ramda';
import { IS_PHONE_BROWSER } from 'reactApp/appHelpers/configHelpers';
import { Tariff } from 'reactApp/types/Billing';
import { isMonthPeriod, isYearPeriod, parsePeriod, simplifyPeriod } from 'reactApp/utils/Period';
import { pretextSPlural } from 'reactApp/utils/pluralHelpers';

export const GROUP_TYPES = {
    period: 'period',
    economy: 'economy',
    pro: 'pro',
};

const SPACE_1GB = 1024 ** 3;

export const SINGLE_QUOTA_REGEX = /.*_single_quota*/;

export const SINGLE_QUOTA_DISKO_REGEX = /.*_single_quota_disko_cloud*/;

export const DEFAULT_PRIMARY_PRODUCT = 137438953472; // 128GB

export const normalizeData = (tariffs) => {
    const list: Record<string, Tariff | undefined> = {};

    Object.keys(tariffs).forEach((id) => {
        const group = tariffs[id];

        const { services, products } = group;
        const space = bytesToNDigits(services.quota, 3);
        const gift = services.gift && {
            space: bytesToNDigits(services.gift.gift_quota, 3),
            period: services.gift.gift_interval,
            id: services.gift.giftible_id,
        };

        list[id] = {
            id,
            turbo: /^pro?\d/.test(products[0].product_id),
            isSingleQuota: SINGLE_QUOTA_REGEX.test(products[0].product_id),
            isPromoCode: products.some((product) => product.is_promo),
            available: products.every((product) => product.available),
            space,
            flags: {
                upload: !!services.flags.UFLAG_PAID_UPLOAD,
                paidFeatures: !!services.flags.UFLAG_PAID_ACCOUNT,
            },
            gift,
            products: products.map((product) => ({
                price: product.price,
                id: product.product_id,
                period: product.period,
                available: product.available,
                isPromo: product.is_promo,
                hasTrial: product.has_trial_period,
                trialPeriod: product.trial_period,
                isProfessional: !!services.pro,
                isDisko: !!services.disko,
                hasDiskO: !!services.disko && !!services.pro,
                hasDiskOAutoRenewal: !!services.disko,
                // Пока считаем что наличие санты это наличие vkCombo
                isVkCombo: !!services.santa,
                santa: services.santa,
                // Триалы у которых есть еще скидочный период
                isForceDiscountTrial: product.has_trial_period && product.has_discount_period,
                // "предоплаченный" триал, не отнимает квоту при отвязке карты / отключеннии автопродления
                isPrepaid: product.is_prepaid,
                hasDiscount: product.has_discount_period,
                discountPeriod: product.discount_period,
                discountPrice: product.discount_price,
                space,
                turbo: /^pro?\d/.test(product.product_id),
                isSingleQuota: SINGLE_QUOTA_REGEX.test(product.product_id),
                gift,
            })),
        };
    });

    return list;
};

export const checkProductListEquals =
    (oldData) =>
    ({ data }) =>
        !equals(normalizeData(data), oldData);

export const PROMO_TARIFFS_REGEX = {
    promoTariffs: /.*_PT$/,
    marketingPromo: /.*_newyear2022_gift_single_quota.*/,
};

export const TRIAL_REGEX = /.*(prepaid|trial)*/;

export const isTrial = (productId?: string) => productId?.includes('prepaid') || productId?.includes('trial');

export const YEAR_COST_DECREASE_REGEX = /W\d{3,4}G1y_2021/;

export const YEAR_COST_DECREASE_SINGLE_QUOTA_REGEX = /W\d{1,4}[TG]1y_.*(single_quota_discount50_cloud)/;

// tempexp_14156-start
export const isTariffForDiscountAb = (tariff, abSpecialDiscounts) =>
    tariff.products.some((p) => isMonthPeriod(p.period)) || (abSpecialDiscounts && abSpecialDiscounts !== 'b' && !IS_PHONE_BROWSER)
        ? !YEAR_COST_DECREASE_SINGLE_QUOTA_REGEX.test(tariff.id)
        : YEAR_COST_DECREASE_SINGLE_QUOTA_REGEX.test(tariff.id);
// tempexp_14156-end

export const isNotSpecialProduct = (product) =>
    !product.products.some((product) => product.hasTrial) &&
    !product.products.some((product) => product.isPromo) &&
    !values(PROMO_TARIFFS_REGEX).some((promoRegex) => promoRegex.test(product.id));

export const SPACE_SIZES = {
    _32: 32 * SPACE_1GB,
    _64: 64 * SPACE_1GB,
    _128: 128 * SPACE_1GB,
    _256: 256 * SPACE_1GB,
    _512: 512 * SPACE_1GB,
    _1024: 1024 * SPACE_1GB,
    _2048: 2048 * SPACE_1GB,
    _4096: 4096 * SPACE_1GB,
};

export const groupDefinition = new Map([
    [
        GROUP_TYPES.economy,
        [
            {
                name: 'Начальные',
                selector: (product) => product.products.every((item) => item.space.original <= SPACE_SIZES._256),
                filterSubProducts: (item) => item,
            },
            {
                name: 'Выгодные',
                selector: (product) =>
                    product.products.every((item) => item.space.original >= SPACE_SIZES._256 && item.space.original <= SPACE_SIZES._1024),
                filterSubProducts: (item) => item,
                isDefault: true,
            },
            {
                name: 'Профессиональные',
                selector: (product) => product.products.every((item) => item.space.original >= SPACE_SIZES._1024),
                filterSubProducts: (item) => item,
            },
        ],
    ],
    [
        GROUP_TYPES.period,
        [
            {
                name: 'Ежемесячная оплата',
                selector: (product) => product.products.some((item) => isMonthPeriod(item.period)),
                filterSubProducts: (item) => isMonthPeriod(item.period),
            },
            {
                name: 'Ежегодная оплата',
                badge: 'ВЫГОДНЕЕ',
                selector: (product) => product.products.some((item) => isYearPeriod(item.period)),
                filterSubProducts: (item) => isYearPeriod(item.period),
                isDefault: true,
            },
        ],
    ],
    [
        GROUP_TYPES.pro,
        [
            {
                name: 'На месяц',
                fullName: 'Тарифы на месяц',
                id: 'month',
                selector: (product) => product.products.some((item) => isMonthPeriod(item.period)),
                filterSubProducts: (item) => isMonthPeriod(item.period),
            },
            {
                name: 'На год',
                fullName: 'Тарифы на год',
                id: 'year',
                isDefault: true,
                selector: (product) => product.products.some((item) => isYearPeriod(item.period)),
                filterSubProducts: (item) => isYearPeriod(item.period),
            },
            {
                name: 'Профессионалам',
                fullName: 'Профессионалам',
                id: 'pro',
                badge: 'новинка',
                selector: (product) => product.products.some((item) => item.hasDiskO),
                filterSubProducts: (item) => item.hasDiskO,
            },
        ],
    ],
]);

export const getPretextPeriodForProduct = (period: string) => {
    const matchedPeriod = parsePeriod(period);
    if (!matchedPeriod) {
        return '';
    }
    const [count] = simplifyPeriod(Number(matchedPeriod[1]), matchedPeriod[2]);

    return pretextSPlural(count + 1);
};

export const filterTariffsByQuotaAndPeriod = (tariff, quota, period) => {
    if (tariff.space.original !== quota) {
        return false;
    }

    return Boolean(tariff.products.find((product) => product.period === period));
};
